import type { Polygon, Position } from "geojson";
import { z } from "zod";

/**
 * EARTH_RADIUS_M is the mean Earth radius in metres, the sphere the parametric
 * zone builders project a metric offset onto. It matches the value the elevation
 * profile geodesy uses (EARTH_RADIUS_KM = 6371), so a circle built here and a
 * distance measured there agree on the same sphere.
 */
const EARTH_RADIUS_M = 6371_000;

/** DEGREE_TO_RADIANS converts a degree (coordinate or bearing) to radians. */
const DEGREE_TO_RADIANS = Math.PI / 180;

/** RADIANS_TO_DEGREE converts a radian back to a degree for the emitted coordinate. */
const RADIANS_TO_DEGREE = 180 / Math.PI;

/** FULL_TURN_DEGREES is one full revolution, the span a full-circle sector covers. */
const FULL_TURN_DEGREES = 360;

/**
 * CIRCLE_SEGMENTS_DEFAULT is how many straight edges approximate a full circle
 * when the caller gives no count. 64 keeps a city-to-region radius visually round
 * without an oversized coordinate ring; the cap bounds a caller-supplied value.
 */
const CIRCLE_SEGMENTS_DEFAULT = 64;
const CIRCLE_SEGMENTS_MIN = 8;
const CIRCLE_SEGMENTS_MAX = 512;

/**
 * ARC_POINTS_DEFAULT is how many points sample a sector/sensor arc when the
 * caller gives no count. 32 traces a smooth arc across a typical bearing span; the
 * cap bounds a caller-supplied value. It counts the points ALONG the arc (the
 * sweep is sampled at arcPoints positions inclusive of both bearings).
 */
const ARC_POINTS_DEFAULT = 32;
const ARC_POINTS_MIN = 2;
const ARC_POINTS_MAX = 512;

/**
 * lngLatSchema is a [lng, lat] center point for a parametric builder, bounded to
 * the valid WGS84 range so an out-of-range coordinate is rejected at the boundary
 * the same way the terrain samplers bound their point.
 */
const lngLatSchema = z.tuple([z.number().min(-180).max(180), z.number().min(-90).max(90)]);

/**
 * LngLat is a [lng, lat] center point, derived from lngLatSchema.
 */
export type LngLat = z.infer<typeof lngLatSchema>;

/**
 * radiusMetersSchema is a strictly-positive radius in metres. A non-positive
 * radius cannot describe an area, so it is rejected rather than collapsing the
 * geometry to its center.
 */
const radiusMetersSchema = z.number().positive();

/**
 * bearingSchema is a compass bearing in degrees (0 = north, clockwise), bounded
 * to a full turn. The sector/sensor builders normalise the start→end sweep
 * internally, so the two bounds only reject a wildly out-of-range value.
 */
const bearingSchema = z.number().min(0).max(FULL_TURN_DEGREES);

/**
 * zoneCircleParamsSchema validates the parametric inputs to a circle zone: a
 * center [lng, lat], a positive radius in metres, and an optional edge count. It
 * is the Zod source of truth shared by the runtime builder, its TS type, and the
 * agent tool's LLM JSON Schema, so "blast radius / danger radius / exclusion
 * radius from a point + radius" is one validated shape end to end.
 */
export const zoneCircleParamsSchema = z.object({
  center: lngLatSchema,
  radiusMeters: radiusMetersSchema,
  segments: z.number().int().min(CIRCLE_SEGMENTS_MIN).max(CIRCLE_SEGMENTS_MAX).optional(),
});

/**
 * ZoneCircleParams is the parametric circle input, derived from
 * zoneCircleParamsSchema.
 */
export type ZoneCircleParams = z.infer<typeof zoneCircleParamsSchema>;

/**
 * zoneSectorParamsSchema validates the parametric inputs to a sector (pie-slice)
 * zone: a center, a positive radius, the start and end compass bearings of the
 * sweep, and an optional arc-point count. It backs "avalanche aspect / wind /
 * search bearing / line-of-sight cone from a center + bearing range + radius".
 */
export const zoneSectorParamsSchema = z.object({
  arcPoints: z.number().int().min(ARC_POINTS_MIN).max(ARC_POINTS_MAX).optional(),
  bearingEnd: bearingSchema,
  bearingStart: bearingSchema,
  center: lngLatSchema,
  radiusMeters: radiusMetersSchema,
});

/**
 * ZoneSectorParams is the parametric sector input, derived from
 * zoneSectorParamsSchema.
 */
export type ZoneSectorParams = z.infer<typeof zoneSectorParamsSchema>;

/**
 * zoneSensorParamsSchema validates the parametric inputs to a sensor (annular
 * sector / swept-coverage) zone: a center, an inner and outer radius, the start
 * and end bearings of the sweep, and an optional arc-point count. A refine
 * enforces the outer radius exceeds the inner so the annulus has positive width.
 * It backs "antenna / drone / observation coverage" — the same swept-polygon
 * family TerraDrawSensorMode draws interactively, expressed as a closed form the
 * agent can specify directly.
 */
export const zoneSensorParamsSchema = z
  .object({
    arcPoints: z.number().int().min(ARC_POINTS_MIN).max(ARC_POINTS_MAX).optional(),
    bearingEnd: bearingSchema,
    bearingStart: bearingSchema,
    center: lngLatSchema,
    innerRadiusMeters: radiusMetersSchema,
    outerRadiusMeters: radiusMetersSchema,
  })
  .refine((params) => params.outerRadiusMeters > params.innerRadiusMeters, {
    message: "sensor outer radius must exceed inner radius",
  });

/**
 * ZoneSensorParams is the parametric sensor input, derived from
 * zoneSensorParamsSchema.
 */
export type ZoneSensorParams = z.infer<typeof zoneSensorParamsSchema>;

/**
 * destinationPoint returns the [lng, lat] reached by travelling distanceMeters
 * from origin along bearingDeg (0 = north, clockwise), on a sphere of radius
 * EARTH_RADIUS_M. It is the spherical law-of-cosines destination formula — the
 * same great-circle model the haversine distance uses inverted — so an offset
 * here is geodesically reasonable at the city-to-region radii a zone covers
 * (well within a degree of the planar approximation the rest of the studio
 * relies on). It is the one primitive every parametric builder offsets from.
 */
export const destinationPoint = (
  origin: LngLat,
  distanceMeters: number,
  bearingDeg: number,
): Position => {
  const angular = distanceMeters / EARTH_RADIUS_M;
  const bearing = bearingDeg * DEGREE_TO_RADIANS;
  const lat1 = origin[1] * DEGREE_TO_RADIANS;
  const lng1 = origin[0] * DEGREE_TO_RADIANS;

  const sinLat1 = Math.sin(lat1);
  const cosLat1 = Math.cos(lat1);
  const sinAngular = Math.sin(angular);
  const cosAngular = Math.cos(angular);

  const lat2 = Math.asin(sinLat1 * cosAngular + cosLat1 * sinAngular * Math.cos(bearing));
  const lng2 =
    lng1 +
    Math.atan2(Math.sin(bearing) * sinAngular * cosLat1, cosAngular - sinLat1 * Math.sin(lat2));

  // Wrap longitude back into [-180, 180] so a circle straddling the antimeridian
  // emits valid coordinates rather than values past ±180.
  const lng = (((lng2 * RADIANS_TO_DEGREE + 540) % 360) + 360) % 360;

  return [lng - 180, lat2 * RADIANS_TO_DEGREE];
};

/**
 * sweepDegrees normalises a start→end bearing sweep to a positive span in
 * [0, 360]: a sweep whose end is at or below its start wraps forward through
 * north (a 350→10 sweep spans 20°, not -340°), and an exactly-equal pair is read
 * as a full turn so "0 to 0" / "180 to 180" describes the whole circle rather
 * than a degenerate slice. The builders walk start + this span so the arc always
 * runs the short clockwise way the bearings name.
 */
const sweepDegrees = (bearingStartDeg: number, bearingEndDeg: number): number => {
  const raw = bearingEndDeg - bearingStartDeg;
  const wrapped = ((raw % FULL_TURN_DEGREES) + FULL_TURN_DEGREES) % FULL_TURN_DEGREES;

  return wrapped === 0 ? FULL_TURN_DEGREES : wrapped;
};

/**
 * closeRing appends the first coordinate to the end of a coordinate list so it
 * forms a closed linear ring (RFC-7946 requires a Polygon ring's first and last
 * positions to be identical). The input must hold at least one coordinate.
 */
const closeRing = (coordinates: Position[]): Position[] => {
  const first = coordinates[0];
  if (!first) {
    return coordinates;
  }

  return [...coordinates, first];
};

/**
 * zoneCircleGeometry builds a GeoJSON Polygon approximating a geodesic circle of
 * radiusMeters around center, sampled at `segments` evenly-spaced bearings. The
 * ring is explicitly closed (first === last). It is the geometry behind a circle
 * zone — a blast / danger / exclusion radius — shared by the agent's parametric
 * propose path and any in-app default; the TerraDraw circle mode draws the same
 * shape interactively.
 */
export const zoneCircleGeometry = (params: ZoneCircleParams): Polygon => {
  const segments = params.segments ?? CIRCLE_SEGMENTS_DEFAULT;
  const step = FULL_TURN_DEGREES / segments;
  const ring: Position[] = [];
  for (let index = 0; index < segments; index += 1) {
    ring.push(destinationPoint(params.center, params.radiusMeters, index * step));
  }

  return { coordinates: [closeRing(ring)], type: "Polygon" };
};

/**
 * zoneSectorGeometry builds a GeoJSON Polygon pie-slice: the center, an arc of
 * `arcPoints` positions swept from bearingStart to bearingEnd at radiusMeters,
 * and back to the center. A full-turn sweep (equal bearings) yields a closed
 * disc. The ring is explicitly closed. It is the geometry behind a sector zone —
 * an avalanche-aspect band, a search-bearing or line-of-sight cone.
 */
export const zoneSectorGeometry = (params: ZoneSectorParams): Polygon => {
  const arcPoints = params.arcPoints ?? ARC_POINTS_DEFAULT;
  const span = sweepDegrees(params.bearingStart, params.bearingEnd);
  const step = span / (arcPoints - 1);

  const ring: Position[] = [params.center];
  for (let index = 0; index < arcPoints; index += 1) {
    ring.push(
      destinationPoint(params.center, params.radiusMeters, params.bearingStart + index * step),
    );
  }

  return { coordinates: [closeRing(ring)], type: "Polygon" };
};

/**
 * zoneSensorGeometry builds a GeoJSON Polygon annular sector (a swept-coverage
 * "sensor" footprint): the outer arc swept from bearingStart to bearingEnd at
 * outerRadiusMeters, then the inner arc swept back at innerRadiusMeters, closing
 * the band. A full-turn sweep yields a full ring (annulus). The ring is
 * explicitly closed. It is the geometry behind a sensor zone — an antenna, drone,
 * or observation coverage footprint — the closed-form expression of the swept
 * polygon TerraDrawSensorMode draws interactively.
 */
export const zoneSensorGeometry = (params: ZoneSensorParams): Polygon => {
  const arcPoints = params.arcPoints ?? ARC_POINTS_DEFAULT;
  const span = sweepDegrees(params.bearingStart, params.bearingEnd);
  const step = span / (arcPoints - 1);

  const ring: Position[] = [];
  // Outer arc, start → end.
  for (let index = 0; index < arcPoints; index += 1) {
    ring.push(
      destinationPoint(params.center, params.outerRadiusMeters, params.bearingStart + index * step),
    );
  }

  // Inner arc, end → start, so the band closes back on itself.
  for (let index = arcPoints - 1; index >= 0; index -= 1) {
    ring.push(
      destinationPoint(params.center, params.innerRadiusMeters, params.bearingStart + index * step),
    );
  }

  return { coordinates: [closeRing(ring)], type: "Polygon" };
};
